import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Download, Upload, FileSpreadsheet, Check, Loader2, AlertTriangle, X, ExternalLink } from 'lucide-react';

const EXPORT_TYPES = [
  { value: 'ingredients_csv', label: 'Ingredientes (CSV)' },
  { value: 'recipes_csv', label: 'Recetas (CSV)' },
  { value: 'ingredients_excel', label: 'Ingredientes (Excel)' },
  { value: 'recipes_excel', label: 'Recetas (Excel)' },
  { value: 'menu_engineering_excel', label: 'Menu Engineering (Excel)' },
];

export default function ImportExport() {
  const { token, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [exportType, setExportType] = useState('ingredients_csv');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvRawData, setCsvRawData] = useState('');
  const fileRef = useRef(null);
  const currency = user?.currency || 'USD';

  const handleExport = async () => {
    setExporting(true);
    try {
      const endpointMap = {
        'ingredients_csv': '/api/reports/ingredients/csv',
        'recipes_csv': '/api/reports/recipes/csv',
        'ingredients_excel': '/api/reports/ingredients/excel',
        'recipes_excel': '/api/reports/recipes/excel',
        'menu_engineering_excel': '/api/reports/menu-engineering/excel',
      };
      const endpoint = endpointMap[exportType];
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const blob = await response.blob();
      const ext = exportType.includes('csv') ? 'csv' : 'xlsx';
      const name = exportType.includes('ingredient') ? 'Ingredientes' : exportType.includes('recipe') ? 'Recetas' : 'MenuEngineering';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `M3_${name}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      setCsvRawData(text);
      // Parse preview
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
        const rows = lines.slice(1, Math.min(6, lines.length)).map(line => {
          const cells = [];
          let current = '';
          let inQuotes = false;
          for (const char of line) {
            if (char === '"') { inQuotes = !inQuotes; }
            else if (char === ',' && !inQuotes) { cells.push(current.trim()); current = ''; }
            else { current += char; }
          }
          cells.push(current.trim());
          return cells;
        });
        setCsvPreview({ headers, rows, totalRows: lines.length - 1 });
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvRawData) return;
    setImporting(true);
    setImportResult(null);
    try {
      const response = await fetch(`${API_URL}/api/import/ingredients/csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_data: csvRawData })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error');
      setImportResult(data);
      setCsvPreview(null);
      setCsvRawData('');
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setCsvPreview(null);
    setCsvRawData('');
    setImportResult(null);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl" data-testid="import-export-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Datos</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">Importar / Exportar</h2>
      </div>

      {/* Export Section */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8" data-testid="export-section">
        <div className="flex items-center gap-3 mb-6">
          <Download size={18} className="text-gold" />
          <h3 className="text-lg font-semibold">Exportar Datos</h3>
        </div>
        <p className="text-xs text-white/40 mb-4">Descarga tus datos en CSV (compatible con Google Sheets) o Excel.</p>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div className="w-full sm:w-64">
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Tipo de exportacion</label>
            <MobileDropdown options={EXPORT_TYPES} value={exportType} onChange={setExportType} testId="export-type-select" />
          </div>
          <button onClick={handleExport} disabled={exporting} data-testid="export-btn"
            className="flex items-center gap-2 px-6 py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? 'Exportando...' : 'Exportar'}
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-3">Usar con Google Sheets</p>
          <div className="flex items-start gap-3 p-4 bg-white/3 border border-white/5">
            <FileSpreadsheet size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-white/50 space-y-1">
              <p>1. Exporta tus datos en formato <strong className="text-white/70">CSV</strong></p>
              <p>2. Abre <a href="https://sheets.google.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">Google Sheets <ExternalLink size={10} /></a></p>
              <p>3. Ve a <strong className="text-white/70">Archivo &gt; Importar &gt; Subir</strong> y selecciona el CSV</p>
              <p>4. Edita tus datos en Google Sheets</p>
              <p>5. Para volver a importar: <strong className="text-white/70">Archivo &gt; Descargar &gt; CSV</strong> y usa la seccion de importar abajo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8" data-testid="import-section">
        <div className="flex items-center gap-3 mb-6">
          <Upload size={18} className="text-emerald-400" />
          <h3 className="text-lg font-semibold">Importar Ingredientes</h3>
        </div>
        <p className="text-xs text-white/40 mb-2">Importa ingredientes desde un archivo CSV. El archivo debe tener las siguientes columnas:</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {['Nombre', 'Categoria', 'Proveedor', 'Precio', 'Unidad', 'Merma %', 'Stock Minimo', 'Notas'].map(col => (
            <span key={col} className="text-[10px] px-2 py-1 bg-white/5 border border-white/10 text-white/50">{col}</span>
          ))}
        </div>

        <input type="file" ref={fileRef} accept=".csv,.txt" onChange={handleFileSelect} className="hidden" />
        
        {!csvPreview && !importResult && (
          <button onClick={() => fileRef.current?.click()} data-testid="select-csv-btn"
            className="w-full py-4 border-2 border-dashed border-white/10 text-white/40 text-sm hover:border-gold/30 hover:text-gold transition-colors flex items-center justify-center gap-2">
            <Upload size={16} /> Seleccionar archivo CSV
          </button>
        )}

        {/* CSV Preview */}
        {csvPreview && (
          <div data-testid="csv-preview">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-white/60">{csvPreview.totalRows} filas encontradas</p>
              <button onClick={cancelImport} className="text-white/30 hover:text-white"><X size={16} /></button>
            </div>
            <div className="overflow-x-auto mb-4 border border-white/10">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-white/5">{csvPreview.headers.map((h, i) => <th key={i} className="text-left py-2 px-3 text-white/40">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {csvPreview.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-white/5">{row.map((cell, ci) => <td key={ci} className="py-2 px-3 text-white/60">{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              {csvPreview.totalRows > 5 && <p className="text-[10px] text-white/20 p-2 text-center">...y {csvPreview.totalRows - 5} filas mas</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={handleImport} disabled={importing} data-testid="import-btn"
                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white text-xs font-bold uppercase tracking-widest hover:bg-emerald-600 transition-colors disabled:opacity-50">
                {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {importing ? 'Importando...' : `Importar ${csvPreview.totalRows} Ingredientes`}
              </button>
              <button onClick={cancelImport} className="px-6 py-3 border border-white/10 text-white/40 text-xs hover:text-white transition-colors">Cancelar</button>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div data-testid="import-result">
            {importResult.error ? (
              <div className="p-4 bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2 text-red-400 mb-2"><AlertTriangle size={14} /> <span className="text-sm font-medium">Error</span></div>
                <p className="text-xs text-white/60">{importResult.error}</p>
              </div>
            ) : (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2 text-emerald-400 mb-2"><Check size={14} /> <span className="text-sm font-medium">Importacion completada</span></div>
                <div className="text-xs text-white/60 space-y-1">
                  <p>{importResult.imported} ingrediente{importResult.imported !== 1 ? 's' : ''} importado{importResult.imported !== 1 ? 's' : ''} exitosamente</p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-amber-400 mb-1">{importResult.errors.length} error(es):</p>
                      {importResult.errors.slice(0, 5).map((err, i) => <p key={i} className="text-white/40">{err}</p>)}
                      {importResult.errors.length > 5 && <p className="text-white/30">+{importResult.errors.length - 5} errores mas</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
            <button onClick={() => { setImportResult(null); }} data-testid="import-done-btn"
              className="mt-4 px-4 py-2 border border-white/10 text-white/40 text-xs hover:text-white transition-colors">
              {importResult.error ? 'Intentar de nuevo' : 'Importar mas'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
